import { Row } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseRowSelectionParams<T> {
  /**
   * Row ids present in the data. Selected ids missing from it are dropped, so a
   * selection can't outlive the rows it points at. Pass the full dataset, not
   * the filtered rows — searching hides rows without removing them.
   */
  availableIds?: Set<string>;
  getRowId: (row: T) => string;
  isLocked: (row: T) => boolean;
}

interface FilteredRowState {
  allUUIDs: Set<string>;
  lockedUUIDs: Set<string>;
  selectableUUIDs: Set<string>;
}

const setsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((value) => b.has(value));

export interface UseRowSelectionReturn<T> {
  clearSelection: () => void;
  filteredCount: number;
  isAllLocked: boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  onFilteredRowsChange: (rows: Row<T>[]) => void;
  selectedCount: number;
  selectedUUIDs: Set<string>;
  toggleAllVisible: () => void;
  toggleSelection: (uuid: string) => void;
  visibleSelectedCount: number;
}

export const useRowSelection = <T>({
  availableIds,
  getRowId,
  isLocked,
}: UseRowSelectionParams<T>): UseRowSelectionReturn<T> => {
  const [selectedUUIDs, setSelectedUUIDs] = useState(new Set<string>());
  const [filteredRows, setFilteredRows] = useState<FilteredRowState>({
    allUUIDs: new Set(),
    lockedUUIDs: new Set(),
    selectableUUIDs: new Set(),
  });

  // Rows can disappear from under a selection — a bulk unbookmark removes
  // exactly the selected rows — which would otherwise strand the bulk bar on a
  // count of rows that no longer exist.
  useEffect(() => {
    if (!availableIds) {
      return;
    }
    setSelectedUUIDs((prev) => {
      const next = new Set([...prev].filter((uuid) => availableIds.has(uuid)));
      return next.size === prev.size ? prev : next;
    });
  }, [availableIds]);

  const selectedCount = selectedUUIDs.size;
  const filteredCount = filteredRows.allUUIDs.size;
  const selectableCount = filteredRows.selectableUUIDs.size;
  const isAllLocked = filteredCount > 0 && selectableCount === 0;

  const visibleSelectedCount = useMemo(
    () =>
      [...filteredRows.selectableUUIDs].filter((uuid) =>
        selectedUUIDs.has(uuid),
      ).length,
    [filteredRows.selectableUUIDs, selectedUUIDs],
  );

  const isAllSelected =
    selectableCount > 0 && visibleSelectedCount === selectableCount;
  const isPartiallySelected =
    visibleSelectedCount > 0 && visibleSelectedCount < selectableCount;

  const clearSelection = useCallback(() => {
    setSelectedUUIDs(new Set());
  }, []);

  const toggleSelection = useCallback(
    (uuid: string) => {
      if (filteredRows.lockedUUIDs.has(uuid)) {
        return;
      }
      setSelectedUUIDs((prev) => {
        const next = new Set(prev);
        if (next.has(uuid)) {
          next.delete(uuid);
        } else {
          next.add(uuid);
        }
        return next;
      });
    },
    [filteredRows.lockedUUIDs],
  );

  const toggleAllVisible = useCallback(() => {
    setSelectedUUIDs((prev) => {
      const next = new Set(prev);
      const { selectableUUIDs } = filteredRows;
      const nextVisibleSelectedCount = [...selectableUUIDs].filter((uuid) =>
        next.has(uuid),
      ).length;
      const shouldDeselectVisible =
        selectableUUIDs.size > 0 &&
        nextVisibleSelectedCount === selectableUUIDs.size;

      for (const uuid of selectableUUIDs) {
        if (shouldDeselectVisible) {
          next.delete(uuid);
        } else {
          next.add(uuid);
        }
      }

      return next;
    });
  }, [filteredRows]);

  const onFilteredRowsChange = useCallback(
    (rows: Row<T>[]) => {
      const allUUIDs = new Set<string>();
      const lockedUUIDs = new Set<string>();
      const selectableUUIDs = new Set<string>();

      for (const row of rows) {
        const uuid = getRowId(row.original);
        allUUIDs.add(uuid);
        if (isLocked(row.original)) {
          lockedUUIDs.add(uuid);
        } else {
          selectableUUIDs.add(uuid);
        }
      }

      setFilteredRows((prev) =>
        setsEqual(prev.allUUIDs, allUUIDs) &&
        setsEqual(prev.lockedUUIDs, lockedUUIDs) &&
        setsEqual(prev.selectableUUIDs, selectableUUIDs)
          ? prev
          : { allUUIDs, lockedUUIDs, selectableUUIDs },
      );
    },
    [getRowId, isLocked],
  );

  return {
    clearSelection,
    filteredCount,
    isAllLocked,
    isAllSelected,
    isPartiallySelected,
    onFilteredRowsChange,
    selectedCount,
    selectedUUIDs,
    toggleAllVisible,
    toggleSelection,
    visibleSelectedCount,
  };
};
